import type { DriverRosterBlockDto, RosterBlockKind, RosterDayStatus } from '@my/directory-api';
import { useDeleteRosterBlock, useDriverRosterBlocks } from '@my/directory-api/hooks';
import { cn, showToast } from '@my/ui';
import { Loader2, Pencil, Trash2 } from 'lucide-react';

const KIND_LABEL: Record<RosterBlockKind, string> = {
  Working: 'Working day',
  Holidays: 'Holidays',
  RestPeriod: 'Rest period',
  SickDay: 'Sick day',
  OtherLeave: 'Other leave',
};

/** "2026-06-15" → "15 Jun"; collapses a single-day range to one date. */
function formatRange(from: string, to: string): string {
  const fmt = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return from === to ? fmt(from) : `${fmt(from)} – ${fmt(to)}`;
}

/**
 * Right-click menu for a driver row: add a working-day / leave block, or remove
 * one of the driver's existing blocks for the shown month.
 */
export function RosterContextMenu({
  organisationId,
  driverResourceId,
  driverName,
  year,
  month,
  x,
  y,
  dayStatus,
  onAddWorking,
  onAddLeave,
  onEditBlock,
  onClose,
}: {
  organisationId: string;
  driverResourceId: string;
  driverName: string;
  /** 1-based month. */
  year: number;
  month: number;
  x: number;
  y: number;
  /** Status of the clicked day (undefined for a row click with no specific day). */
  dayStatus?: RosterDayStatus;
  onAddWorking: () => void;
  onAddLeave: () => void;
  onEditBlock: (block: DriverRosterBlockDto) => void;
  onClose: () => void;
}) {
  // Only offer actions that change the day: no "add working" on a working day,
  // and no "add leave" on a day that's already leave or a day off.
  const canAddWorking = dayStatus !== 'W';
  const canAddLeave = dayStatus === undefined || dayStatus === 'W';
  const blocksQuery = useDriverRosterBlocks(organisationId, driverResourceId, year, month);
  const deleteBlock = useDeleteRosterBlock();

  const remove = async (id: string) => {
    try {
      await deleteBlock.mutateAsync(id);
      showToast({ type: 'success', message: 'Block removed.' });
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : 'Failed to remove block.' });
    }
  };

  const blocks = blocksQuery.data ?? [];

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="fixed z-50 w-56 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        style={{ top: y, left: x }}
      >
        <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          {driverName}
        </div>
        {canAddWorking && <MenuItem onClick={onAddWorking}>Add working day</MenuItem>}
        {canAddLeave && <MenuItem onClick={onAddLeave}>Add leave</MenuItem>}

        <div className="my-1 border-t border-slate-100" />
        <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          Blocks this month
        </div>
        {blocksQuery.isLoading ? (
          <div className="px-3 py-1.5 text-xs text-slate-400">Loading…</div>
        ) : blocks.length === 0 ? (
          <div className="px-3 py-1.5 text-xs italic text-slate-400">None</div>
        ) : (
          <ul className="max-h-48 overflow-auto">
            {blocks.map((b: DriverRosterBlockDto) => (
              <li key={b.id} className="flex items-center gap-2 px-3 py-1 hover:bg-slate-50">
                <span className="min-w-0 flex-1 truncate text-xs text-slate-600">
                  {KIND_LABEL[b.kind]} · {formatRange(b.fromDate, b.toDate)}
                </span>
                <button
                  type="button"
                  onClick={() => onEditBlock(b)}
                  className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Edit block"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(b.id)}
                  disabled={deleteBlock.isPending}
                  className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                  aria-label="Remove block"
                >
                  {deleteBlock.isPending && deleteBlock.variables === b.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="my-1 border-t border-slate-100" />
        <MenuItem onClick={onClose} muted>
          Cancel
        </MenuItem>
      </div>
    </>
  );
}

function MenuItem({
  children,
  onClick,
  muted,
}: {
  children: React.ReactNode;
  onClick: () => void;
  muted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50',
        muted ? 'text-slate-400' : 'text-slate-700',
      )}
    >
      {children}
    </button>
  );
}
